import { gpuRenderer } from './webgl'

/**
 * How much of the scene a machine can afford. Chosen once from what the browser says about
 * the device, remembered when the frame rate forces a step down, never stepped up again on a
 * later visit (a machine that struggled once will struggle again).
 */
export type Tier = 'high' | 'mid' | 'low'

export interface TierKnobs {
  /** Resolution scale: where it starts, how high the frame-time adaption may take it, how low it may fall. */
  pixelRatio: { start: number; max: number; min: number }
  /** Multisampling of the post-processing buffer; 0 means no post-processing at all (and canvas antialiasing instead). */
  msaa: number
  bloom: boolean
  shadowMapSize: number
  /** Ring tube and ring tessellation. */
  torusSegments: [number, number]
  /** Most rings a viewport gets. The pile's feel is tuned at 150: only the low tier thins it. */
  ringCap: number
  /** At rest, draw one frame in this many. */
  idleRedrawEvery: number
  powerPreference: 'default' | 'low-power'
}

const KNOBS: Record<Tier, TierKnobs> = {
  high: {
    pixelRatio: { start: 1.5, max: 2, min: 1 },
    msaa: 4,
    bloom: true,
    shadowMapSize: 1024,
    torusSegments: [16, 36],
    ringCap: 150,
    idleRedrawEvery: 15,
    powerPreference: 'default',
  },
  // The same picture as high; only the resolution policy is more careful, and that is what
  // keeps the frame rate: the look is never the thing that gives.
  mid: {
    pixelRatio: { start: 1.25, max: 1.75, min: 0.8 },
    msaa: 4,
    bloom: true,
    shadowMapSize: 1024,
    torusSegments: [16, 36],
    ringCap: 150,
    idleRedrawEvery: 15,
    powerPreference: 'default',
  },
  low: {
    pixelRatio: { start: 1, max: 1, min: 0.6 },
    msaa: 0,
    bloom: false,
    shadowMapSize: 512,
    torusSegments: [10, 24],
    ringCap: 90,
    idleRedrawEvery: 60,
    powerPreference: 'low-power',
  },
}

const STORAGE_KEY = 'hc-tier-2'
const ORDER: Tier[] = ['high', 'mid', 'low']
let picked: Tier | null = null

/** A remembered step-down holds for this long: a slow afternoon should not dim the site for good. */
const REMEMBER_MS = 24 * 60 * 60 * 1000

const remembered = (): Tier | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const [v, at] = raw.split('@')
    if (Date.now() - Number(at ?? 0) > REMEMBER_MS) return null
    return v === 'mid' || v === 'low' ? v : null
  } catch {
    return null
  }
}

/** The tier for this machine: from its cores, memory and GPU, capped by what a past visit learnt. */
export function pickTier(): Tier {
  if (picked) return picked
  const nav = navigator as Navigator & { deviceMemory?: number }
  const cores = nav.hardwareConcurrency ?? 4
  const memory = nav.deviceMemory ?? 8
  const gpu = gpuRenderer()
  const weakGpu =
    /Intel.*(HD|UHD) Graphics [1-6]\d\d\b|Mali-(4|T|G5[0-7])|Adreno \(TM\) [3-5]\d\d|PowerVR|SwiftShader|llvmpipe/i.test(
      gpu
    )
  const touch = !matchMedia('(hover: hover) and (pointer: fine)').matches
  // Low is for hardware that is weak on its face; mid for small machines and phones; the rest
  // start high, and the frame-time adaption takes it from there.
  let tier: Tier = cores <= 2 || memory <= 2 || weakGpu ? 'low' : (cores <= 4 && memory <= 4) || touch ? 'mid' : 'high'
  const saved = remembered()
  if (saved && ORDER.indexOf(saved) > ORDER.indexOf(tier)) tier = saved
  picked = tier
  return tier
}

export const tierKnobs = (tier: Tier = pickTier()): TierKnobs => KNOBS[tier]

/** The frame rate could not be held even at the lowest resolution: remember a lower tier for next time. */
export function rememberLowerTier(): void {
  // The development server is slower than the real site: what it measures is not the machine.
  if (import.meta.env.DEV) return
  const next = ORDER[Math.min(ORDER.length - 1, ORDER.indexOf(pickTier()) + 1)]
  try {
    localStorage.setItem(STORAGE_KEY, `${next}@${Date.now()}`)
  } catch {
    // Private mode or storage blocked: the next visit decides afresh.
  }
}

/**
 * Whether the connection asks for less: Save-Data, or a 2G network. Chrome's "3g" is not
 * trusted: it is a round-trip estimate that a VPN or a busy office network earns too.
 */
export function wantsLightPage(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection
  return !!c?.saveData || /(^|-)2g$/.test(c?.effectiveType ?? '')
}
